import { Alert, Stack, Switch, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import type { FailoverStatusDto } from "@/types/ai-assistant-failover";
import type { ProviderMode } from "@/types/ai-assistant-provider-mode";
import { fetchFailover, saveFailover } from "./ai-assistant.api";
import { alertLines, failoverSwitchBlocked } from "./failover.logic";

interface Props {
	/** Mode tersimpan — menentukan profil Cadangan yang harus siap. */
	mode: ProviderMode;
	disabled: boolean;
	cryptoConfigured: boolean;
	/** Berubah saat kunci/mode tersimpan, memicu muat ulang kesiapan Cadangan. */
	reloadWhen: unknown;
}

/** Sakelar "Pindah otomatis ke Cadangan" di kartu Mode penyedia (bawaan mati). */
export function FailoverSwitch({
	mode,
	disabled,
	cryptoConfigured,
	reloadWhen,
}: Props) {
	const [status, setStatus] = useState<FailoverStatusDto | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reloadWhen sengaja jadi pemicu muat ulang.
	useEffect(() => {
		const controller = new AbortController();
		fetchFailover(controller.signal)
			.then(setStatus)
			.catch((err: unknown) => {
				if (err instanceof Error && err.name === "AbortError") return;
				setError(
					err instanceof Error ? err.message : "Gagal memuat pindah otomatis",
				);
			});
		return () => controller.abort();
	}, [reloadWhen]);

	if (!status) {
		return error ? (
			<Text size="sm" c="red">
				{error}
			</Text>
		) : null;
	}

	const blocked = failoverSwitchBlocked(status, mode, cryptoConfigured);
	const toggle = async (enabled: boolean) => {
		setSaving(true);
		setError(null);
		try {
			setStatus(await saveFailover({ enabled }));
		} catch (err) {
			setError(err instanceof Error ? err.message : "Gagal menyimpan");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Stack gap="xs">
			<Switch
				label="Pindah otomatis ke Cadangan"
				description="Bila profil Utama gangguan, kuotanya habis, atau kuncinya ditolak, giliran dijawab profil Cadangan. Bawaan mati."
				checked={status.enabled}
				disabled={disabled || saving || (!status.enabled && blocked !== null)}
				onChange={(e) => toggle(e.currentTarget.checked)}
			/>
			{!status.enabled && blocked && (
				<Text size="sm" c="red">
					{blocked}
				</Text>
			)}
			{error && (
				<Text size="sm" c="red">
					{error}
				</Text>
			)}
			{alertLines(status.recentAlerts).map((line) => (
				<Alert
					key={line}
					color="yellow"
					icon={<IconAlertTriangle size={16} />}
					p="xs"
				>
					{line}
				</Alert>
			))}
		</Stack>
	);
}
